import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { edgeFnUrl } from '@saas-boilerplate/shared';

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

type Result =
  | { kind: 'loading' }
  | { kind: 'approved' }
  | { kind: 'cancelled' }
  | { kind: 'already'; status: string; previous?: string | null }
  | { kind: 'error'; message: string };

export function ApproveRecapPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const action = (params.get('action') || '').toLowerCase();

  const [result, setResult] = useState<Result>({ kind: 'loading' });

  useEffect(() => {
    (async () => {
      if (!token) {
        setResult({ kind: 'error', message: 'This link is missing its token.' });
        return;
      }
      if (action !== 'approve' && action !== 'cancel') {
        setResult({ kind: 'error', message: 'Unknown action.' });
        return;
      }
      try {
        const res = await fetch(edgeFnUrl('approval-action'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(ANON_KEY ? { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` } : {}),
          },
          body: JSON.stringify({ token }),
        });
        const json = await res.json();
        if (!res.ok) {
          setResult({ kind: 'error', message: json?.error || `HTTP ${res.status}` });
          return;
        }
        if (json.already_decided) {
          setResult({ kind: 'already', status: json.current_status, previous: json.previous_decision });
          return;
        }
        if (json.action === 'approve') setResult({ kind: 'approved' });
        else if (json.action === 'cancel') setResult({ kind: 'cancelled' });
        else setResult({ kind: 'error', message: 'Unexpected response.' });
      } catch (e: any) {
        setResult({ kind: 'error', message: e?.message || 'Network error.' });
      }
    })();
  }, [token, action]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        {result.kind === 'loading' && (
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Recording your decision…</span>
          </CardContent>
        )}
        {result.kind === 'approved' && (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                Recap approved
              </CardTitle>
              <CardDescription>
                The recap is being sent to recipients now. You'll see it land in your inbox along with
                everyone else's.
              </CardDescription>
            </CardHeader>
          </>
        )}
        {result.kind === 'cancelled' && (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <XCircle className="h-5 w-5" />
                Recap cancelled
              </CardTitle>
              <CardDescription>
                The recap will not be sent. The meeting record itself remains in upstand.ai for anyone
                who navigates to it directly.
              </CardDescription>
            </CardHeader>
          </>
        )}
        {result.kind === 'already' && (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <AlertCircle className="h-5 w-5" />
                Already decided
              </CardTitle>
              <CardDescription>
                This recap was already {result.previous || result.status}. No change made.
              </CardDescription>
            </CardHeader>
          </>
        )}
        {result.kind === 'error' && (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Could not record decision
              </CardTitle>
              <CardDescription>{result.message}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                If this link is older than a week it has expired. Open the meeting in upstand.ai to
                approve or cancel directly.
              </p>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
